import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import type { IDatasource } from "ag-grid-community";
import { useNavigate } from "react-router-dom";
import "./Acquirers.css";
import "../transaction-monitoring/TransactionMonitoring.css";
import { BsPlusCircle } from "react-icons/bs";
import { Button } from "../../components/ui/button";
import StatusBadge from "../../components/shared/StatusBadge";
import {
  formatEntityStatusLabel,
  toApiEntityStatus,
} from "../../utils/status";
import { showToast } from "../../store/toastStore";
import DeleteConfirmDialog, { type ConfirmDetailRow } from "../../components/shared/DeleteConfirmDialog";
import StatusUpdateDialog from "../../components/shared/StatusUpdateDialog";
import { DataGrid, type ColDef, type GridApi } from "../../components/ui/table";
import { ActiveGridFiltersBar } from "../../components/shared/agGridCells";
import { useAsyncEffect } from "../../hooks/useAsyncEffect";
import {
  GridColumnFilterPanel,
  formatDateRangeFilterLabel,
} from "../../components/shared/GridColumnFilterPanel";
import { commonTextFilterParams } from "../../lib/agGridOptions";
import { acquirerGridOptions, GRID_PAGE_SIZE, GRID_PAGE_SIZE_SELECTOR } from "./table/gridOptions";
import AcquirerFilters from "./components/AcquirerFilters";
import AcquirerToolbar from "./components/AcquirerToolbar";
import {
  deleteAcquirer,
  fetchAcquirersGrid,
  fetchAcquirersList,
  fetchCountryCurrencyLookups,
  updateAcquirerStatus,
} from "./api/api";
import { buildAcquirerGridRows, buildAcquirersRequest, mapAgGridFilterModelToAcquirerFilters } from './utils/utils';
import { downloadExcelExport, EXPORT_TYPES } from '../../utils/backendExport';
import { ACCENT_COLORS, SEARCH_DEBOUNCE_MS } from './constants/constants';
import { MODAL_TITLE_TO_FILTER_KEY } from './constants/constants';
import { type AcquirerColumnFilterKey, type AcquirerColumnFilters, type AcquirerGridContext, type AcquirerGridRow } from './models/types';
import { EMPTY_ACQUIRER_COLUMN_FILTERS } from './constants/constants';
import { Card } from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { cn } from "../../lib/utils";
import { type ActiveGridFilter } from "../../utils/gridColumnFilters";
import {
  ACQUIRER_COLUMN_LABELS,
  buildAcquirerColumnFilterFields,
  collectAcquirerCurrencyFilterOptions,
  createAcquirerColumnDefs,
} from "./table/columnDefs";

function buildAcquirerDeleteDetails(acq: any): ConfirmDetailRow[] {
  const name = acq.name || "Unknown";
  const methods: string[] = acq.supportedPaymentMethods || [];
  const since = acq.createdAt
    ? new Date(acq.createdAt).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

  return [
    { label: "Acquirer", value: name },
    { label: "Code", value: acq.code || "—" },
    { label: "Country", value: acq.country || "—" },
    { label: "Status", value: <StatusBadge status={acq.status} /> },
    { label: "Contact Email", value: acq.contactEmail || acq.email || "—" },
    {
      label: "Payment Methods",
      value: methods.length > 0 ? methods.join(", ") : "—",
    },
    { label: "Terminals", value: String(acq.totalTerminals ?? 0) },
    { label: "Since", value: since },
  ];
}

function buildAcquirerStatusDetails(acq: any): ConfirmDetailRow[] {
  const name = acq.name || "Unknown";
  return [
    { label: "Acquirer", value: name },
    { label: "Code", value: acq.code || "—" },
    { label: "Country", value: acq.country || "—" },
    { label: "Contact Email", value: acq.contactEmail || acq.email || "—" },
  ];
}

const ACQUIRER_GRID_DEFAULT_COL_DEF = {
  suppressAutoSize: true,
  filter: {
    component: 'agTextColumnFilter',
    doesFilterPass: () => true,
  },
  filterParams: {
    ...commonTextFilterParams,
    filterOptions: ['contains'],
  },
};

const NON_NAVIGABLE_COLUMNS = new Set([
  ACQUIRER_COLUMN_LABELS.paymentMethods,
  ACQUIRER_COLUMN_LABELS.status,
  ACQUIRER_COLUMN_LABELS.trafficTypes,
  ACQUIRER_COLUMN_LABELS.currencies,
  ACQUIRER_COLUMN_LABELS.terminalNames,
  "Action",
]);

const Acquirers: React.FC = () => {
  const navigate = useNavigate();
  const [acquirers, setAcquirers] = useState<any[]>([]);
  const [statsAcquirers, setStatsAcquirers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tablePageSize, setTablePageSize] = useState(GRID_PAGE_SIZE);
  const gridApiRef = useRef<GridApi<AcquirerGridRow> | null>(null);
  const [exporting, setExporting] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [columnFilters, setColumnFilters] = useState<AcquirerColumnFilters>(
    EMPTY_ACQUIRER_COLUMN_FILTERS,
  );
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  // Bumped when AG Grid's header filters change so ActiveGridFiltersBar can re-read
  // getFilterModel() without putting those values into React state.
  const [gridFilterEpoch, setGridFilterEpoch] = useState(0);
  // When true, the next columnFilters-driven datasource reset is skipped because the
  // change originated from clearing/syncing the AG Grid filter model itself.
  const skipDatasourceResetRef = useRef(false);
  const [modalData, setModalData] = useState<{
    title: string;
    items: string[];
    isCurrency: boolean;
  } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [statusTarget, setStatusTarget] = useState<any | null>(null);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  // currencyById: display labels; countryIdsByCurrency: expand filter codes → API countryIds
  const [currencyById, setCurrencyById] = useState<Record<number, string>>({});
  const [countryIdsByCurrency, setCountryIdsByCurrency] = useState<Record<string, number[]>>({});
  const tableFiltersRef = useRef({
    filterStatus,
    search: debouncedSearch,
    columnFilters,
    countryIdsByCurrency,
  });

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(search);
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timeout);
  }, [search]);

  useAsyncEffect(async (isActive) => {
    try {
      const lookups = await fetchCountryCurrencyLookups();
      if (!isActive()) return;
      const nextCurrencyById: Record<number, string> = {};
      const nextCountryIdsByCurrency: Record<string, number[]> = {};
      lookups.forEach((country: { autoId: number; currency: string }) => {
        if (country.autoId > 0 && country.currency) {
          nextCurrencyById[country.autoId] = country.currency;
          const existing = nextCountryIdsByCurrency[country.currency] ?? [];
          if (!existing.includes(country.autoId)) {
            nextCountryIdsByCurrency[country.currency] = [...existing, country.autoId];
          }
        }
      });
      setCurrencyById(nextCurrencyById);
      setCountryIdsByCurrency(nextCountryIdsByCurrency);
    } catch (err) {
      console.error("Failed to fetch country currency lookups:", err);
    }
  }, []);

  const handleShowMore = useCallback(
    (title: string, items: string[], isCurrency?: boolean) => {
      setModalData({ title, items, isCurrency: isCurrency ?? false });
    },
    [],
  );

  const handleColumnFilter = useCallback(
    (key: AcquirerColumnFilterKey, value: string) => {
      setColumnFilters((prev) => {
        const current = prev[key] ?? [];
        const normalized = value.trim().toLowerCase();
        const exists = current.some(
          (item) => item.trim().toLowerCase() === normalized,
        );
        const next = exists
          ? current.filter((item) => item.trim().toLowerCase() !== normalized)
          : [...current, value];

        return {
          ...prev,
          [key]: next.length ? next : null,
        };
      });
    },
    [],
  );

  const handleRemoveColumnFilter = useCallback((key: string) => {
    tableFiltersRef.current = {
      ...tableFiltersRef.current,
      columnFilters: {
        ...tableFiltersRef.current.columnFilters,
        [key]: null,
      },
    };
    setColumnFilters((prev) => ({
      ...prev,
      [key]: null,
    }));
    const api = gridApiRef.current;
    if (api?.getColumnFilterModel(key)) {
      skipDatasourceResetRef.current = true;
      void api.setColumnFilterModel(key, null).then(() => {
        api.onFilterChanged();
      });
    }
  }, []);

  const handleClearColumnFilters = useCallback(() => {
    tableFiltersRef.current = {
      ...tableFiltersRef.current,
      columnFilters: EMPTY_ACQUIRER_COLUMN_FILTERS,
    };
    setColumnFilters(EMPTY_ACQUIRER_COLUMN_FILTERS);
    const api = gridApiRef.current;
    if (api && Object.keys(api.getFilterModel() ?? {}).length > 0) {
      skipDatasourceResetRef.current = true;
      api.setFilterModel(null);
      api.onFilterChanged();
    }
  }, []);

  const handleApplyColumnFilters = useCallback(
    (values: Record<string, string[] | null>) => {
      const nextColumnFilters = {
        ...EMPTY_ACQUIRER_COLUMN_FILTERS,
        ...(Object.fromEntries(
          (Object.keys(EMPTY_ACQUIRER_COLUMN_FILTERS) as AcquirerColumnFilterKey[]).map(
            (key) => [key, values[key]?.length ? values[key] : null],
          ),
        ) as AcquirerColumnFilters),
      };
      tableFiltersRef.current = {
        ...tableFiltersRef.current,
        columnFilters: nextColumnFilters,
      };
      gridApiRef.current?.setFilterModel(null);
      setColumnFilters(nextColumnFilters);
    },
    [],
  );

  const handleModalItemFilter = useCallback(
    (title: string, item: string) => {
      const key = MODAL_TITLE_TO_FILTER_KEY[title];
      if (key === "currencies") {
        const currency = item.trim().toUpperCase();
        if (currency) handleColumnFilter(key, currency);
      } else if (key) {
        handleColumnFilter(key, item);
      }
      setModalData(null);
    },
    [handleColumnFilter],
  );

  useEffect(() => {
    tableFiltersRef.current = {
      filterStatus,
      search: debouncedSearch,
      columnFilters,
      countryIdsByCurrency,
    };
  }, [filterStatus, debouncedSearch, columnFilters, countryIdsByCurrency]);

  const tableDatasource = useMemo<IDatasource>(
    () => ({
      getRows: async (params) => {
        const pageSize = params.endRow - params.startRow;
        const page = pageSize > 0 ? Math.floor(params.startRow / pageSize) : 0;
        setLoading(true);
        setError(null);
        try {
          const rawFilterModel =
            (params.filterModel as Record<string, unknown> | null | undefined) ??
            (params.api?.getFilterModel() as Record<string, unknown> | null | undefined) ??
            null;
          const nativeColumnFilters = mapAgGridFilterModelToAcquirerFilters(rawFilterModel);
          const mergedColumnFilters: AcquirerColumnFilters = {
            ...tableFiltersRef.current.columnFilters,
            ...nativeColumnFilters,
          };
          const { rows, totalElements } = await fetchAcquirersList(
            buildAcquirersRequest({
              filterStatus: tableFiltersRef.current.filterStatus,
              search: tableFiltersRef.current.search,
              columnFilters: mergedColumnFilters,
              countryIdsByCurrency: tableFiltersRef.current.countryIdsByCurrency,
              page,
              size: pageSize,
            }),
          );
          setAcquirers(rows);
          params.successCallback(buildAcquirerGridRows(rows, ACCENT_COLORS), totalElements);
        } catch (err: any) {
          setError("Error connecting to API");
          setAcquirers([]);
          console.error(err);
          params.failCallback();
        } finally {
          setLoading(false);
        }
      },
    }),
    [],
  );

  useEffect(() => {
    if (skipDatasourceResetRef.current) {
      skipDatasourceResetRef.current = false;
      return;
    }
    gridApiRef.current?.setGridOption("datasource", tableDatasource);
  }, [filterStatus, debouncedSearch, columnFilters, countryIdsByCurrency, tableDatasource]);

  const fetchStatsAcquirers = useCallback(async () => {
    try {
      const { rows } = await fetchAcquirersGrid({
        filterStatus: 'All',
        search: '',
        columnFilters: EMPTY_ACQUIRER_COLUMN_FILTERS,
      });
      setStatsAcquirers(rows);
    } catch (err) {
      console.error("Failed to fetch acquirer stats:", err);
    }
  }, []);

  const fetchAcquirers = useCallback(async () => {
    gridApiRef.current?.refreshInfiniteCache();
    await fetchStatsAcquirers();
  }, [fetchStatsAcquirers]);

  useAsyncEffect(async (isActive) => {
    await fetchStatsAcquirers();
    if (!isActive()) return;
  }, [fetchStatsAcquirers]);

  const handleEdit = (id: number) => {
    navigate(`/dashboard/acquirers/edit/${id}`);
  };

  const handleDeleteClick = (acq: any, e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDeleteError(null);
    setDeleteTarget(acq);
  };

  const closeDeleteDialog = () => {
    if (deleting) return;
    setDeleteTarget(null);
    setDeleteError(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    const acquirerName = deleteTarget.name;
    setDeleting(true);
    setDeleteError(null);
    setError(null);
    try {
      await deleteAcquirer(deleteTarget.id);
      setDeleteTarget(null);
      showToast(`Acquirer "${acquirerName}" has been deleted.`, "success");
      await fetchAcquirers();
    } catch (err: any) {
      console.error("Failed to delete acquirer:", err);
      const message = err.response?.data?.message || "Failed to delete acquirer";
      setDeleteError(message);
      showToast(message, "error");
    } finally {
      setDeleting(false);
    }
  };

  const handleStatusClick = (acq: any, e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    setStatusError(null);
    setStatusTarget(acq);
    setSelectedStatus(toApiEntityStatus(acq.status));
  };

  const closeStatusDialog = () => {
    if (statusUpdating) return;
    setStatusTarget(null);
    setStatusError(null);
  };

  const confirmStatusUpdate = async () => {
    if (!statusTarget) return;

    const acquirerName = statusTarget.name;
    const statusLabel = formatEntityStatusLabel(selectedStatus);
    setStatusUpdating(true);
    setStatusError(null);
    setError(null);
    try {
      await updateAcquirerStatus(statusTarget.id, selectedStatus);
      setStatusTarget(null);
      showToast(`${acquirerName} status updated to ${statusLabel}.`, "success");
      await fetchAcquirers();
    } catch (err: any) {
      console.error("Failed to update acquirer status:", err);
      const message = err.response?.data?.message || "Failed to update acquirer status";
      setStatusError(message);
      showToast(message, "error");
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleCreate = () => {
    navigate("/dashboard/acquirers/create");
  };

  const handleGridFilterChanged = useCallback(() => {
    setGridFilterEpoch((n) => n + 1);
  }, []);

  const handleGridReady = useCallback((event: { api: GridApi<AcquirerGridRow> }) => {
    gridApiRef.current = event.api;
  }, []);

  const handleTablePaginationChanged = useCallback(() => {
    const api = gridApiRef.current;
    if (!api) return;
    const newSize = api.paginationGetPageSize();
    if (newSize && newSize !== tablePageSize) {
      setTablePageSize(newSize);
    }
  }, [tablePageSize]);

  const mergedColumnFilters = useMemo<AcquirerColumnFilters>(() => {
    void gridFilterEpoch;
    const nativeColumnFilters = mapAgGridFilterModelToAcquirerFilters(
      gridApiRef.current?.getFilterModel() as Record<string, unknown> | null | undefined,
    );
    return {
      ...columnFilters,
      ...nativeColumnFilters,
    };
  }, [columnFilters, gridFilterEpoch]);

  const activeColumnFilters = useMemo<ActiveGridFilter[]>(() => {
    return (Object.entries(mergedColumnFilters) as [AcquirerColumnFilterKey, string[] | null][])
      .filter(([, value]) => Boolean(value?.length))
      .map(([key, value]) => ({
        key,
        label: ACQUIRER_COLUMN_LABELS[key],
        value:
          key === "createdAt"
            ? formatDateRangeFilterLabel(value)
            : (value as string[]).join(", "),
      }));
  }, [mergedColumnFilters]);

  const columnFilterFields = useMemo(() => {
    const sourceRows = (statsAcquirers.length > 0 ? statsAcquirers : acquirers) as Array<
      Record<string, unknown>
    >;
    const fields = buildAcquirerColumnFilterFields(sourceRows);
    return fields.map((field) => {
      const withCurrencyOptions =
        field.key === "currencies"
          ? {
              ...field,
              options: collectAcquirerCurrencyFilterOptions(sourceRows, currencyById),
            }
          : field;
      if (withCurrencyOptions.type === "dateRange") return withCurrencyOptions;
      const applied = mergedColumnFilters[field.key as AcquirerColumnFilterKey];
      if (!applied?.length) return withCurrencyOptions;
      const optionValues = withCurrencyOptions.options.map((option) =>
        typeof option === "string" ? option : option.value,
      );
      const missing = applied.filter((value) => !optionValues.includes(value));
      if (!missing.length) return withCurrencyOptions;
      return {
        ...withCurrencyOptions,
        options: [...withCurrencyOptions.options, ...missing],
      };
    });
  }, [acquirers, statsAcquirers, currencyById, mergedColumnFilters]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const nativeColumnFilters = mapAgGridFilterModelToAcquirerFilters(
        gridApiRef.current?.getFilterModel() as Record<string, unknown> | null | undefined,
      );
      await downloadExcelExport({
        type: EXPORT_TYPES.ACQUIRERS,
        fileNamePrefix: 'acquirers',
        errorMessage: 'Failed to export acquirers.',
        payload: buildAcquirersRequest({
          filterStatus,
          search: debouncedSearch,
          columnFilters: {
            ...columnFilters,
            ...nativeColumnFilters,
          },
          countryIdsByCurrency,
        }),
      });
    } finally {
      setExporting(false);
    }
  };

  const acquirerColumnDefs = useMemo<ColDef<AcquirerGridRow>[]>(
    () => createAcquirerColumnDefs(),
    [],
  );

  const acquirerGridContext = useMemo<AcquirerGridContext>(
    () => ({
      onShowMore: handleShowMore,
      onColumnFilter: handleColumnFilter,
      columnFilters: mergedColumnFilters,
      currencyById,
      onEdit: handleEdit,
      onStatus: handleStatusClick,
      onDelete: handleDeleteClick,
    }),
    [mergedColumnFilters, currencyById, handleShowMore, handleColumnFilter],
  );

  const handleCellClicked = useCallback(
    (event: { column?: { getColDef: () => { headerName?: string } }; data?: AcquirerGridRow }) => {
      const header = event.column?.getColDef().headerName;
      if (header && NON_NAVIGABLE_COLUMNS.has(header)) return;
      if (event.data) handleEdit(event.data.id);
    },
    [],
  );

  const acquirerGridOptionsMemo = useMemo(
    () => ({
      ...acquirerGridOptions,
      getRowId: (params: { data?: AcquirerGridRow }) => String(params.data?.id ?? ""),
      onCellClicked: handleCellClicked,
      onPaginationChanged: handleTablePaginationChanged,
      onFilterChanged: handleGridFilterChanged,
    }),
    [handleCellClicked, handleTablePaginationChanged, handleGridFilterChanged],
  );

  const gridLoading = loading;

  return (
    <div className="tm-page dh-page-fill">
      <header className="tm-console-header">
        <div className="tm-console-header-row">
          <div className="tm-console-header-copy">
            <h1 className="tm-console-title">Acquirers</h1>
          </div>
          <div className="tm-console-header-actions">
            <Button
              type="button"
              size="sm"
              className="tm-console-action-btn gap-1.5"
              onClick={handleCreate}
            >
              <BsPlusCircle className="h-3 w-3 shrink-0" aria-hidden />
              Create Acquirer
            </Button>
          </div>
        </div>
      </header>

      {error ? <div className="tm-error-banner">{error}</div> : null}

      <div className="dh-table-section tm-table-sections">
        <Card className="tm-table-card tm-table-toolbar-card">
          <div className="tm-table-panel">
            <div className="tm-table-toolbar-row">
              <AcquirerFilters
                filterStatus={filterStatus}
                onFilterStatusChange={setFilterStatus}
                loading={gridLoading}
                statsAcquirers={statsAcquirers}
              />
              <div className="tm-table-toolbar-actions">
                <AcquirerToolbar
                  search={search}
                  onSearchChange={setSearch}
                  activeColumnFilterCount={activeColumnFilters.length}
                  onOpenFilters={() => setFilterPanelOpen(true)}
                  onExport={handleExport}
                  exporting={exporting}
                  loading={gridLoading}
                  hasRows={acquirers.length > 0}
                />
              </div>
            </div>
          </div>
        </Card>
        <Card className="tm-table-card tm-table-grid-card overflow-hidden">
          <ActiveGridFiltersBar
            filters={activeColumnFilters}
            onRemove={handleRemoveColumnFilter}
            onClearAll={handleClearColumnFilters}
          />
          <DataGrid<AcquirerGridRow>
            rowModelType="infinite"
            datasource={tableDatasource}
            cacheBlockSize={tablePageSize}
            columnDefs={acquirerColumnDefs}
            context={acquirerGridContext}
            loading={gridLoading}
            pagination
            paginationPageSize={tablePageSize}
            paginationPageSizeSelector={GRID_PAGE_SIZE_SELECTOR}
            height="100%"
            className="tm-ag-grid"
            onGridReady={handleGridReady}
            defaultColDef={ACQUIRER_GRID_DEFAULT_COL_DEF}
            gridOptions={acquirerGridOptionsMemo}
          />
        </Card>
      </div>

      <Dialog
        open={modalData !== null}
        onOpenChange={(open) => {
          if (!open) setModalData(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{modalData?.title}</DialogTitle>
            <DialogDescription>
              Click an item to add or remove it from the column filter.
            </DialogDescription>
          </DialogHeader>
          <div className="flex max-h-[300px] flex-wrap gap-2 overflow-y-auto pr-1">
            {(modalData?.items ?? []).map((item) => (
              <button
                key={item}
                type="button"
                className={cn(
                  "acq-mid-chip m-0 cursor-pointer",
                  modalData?.isCurrency &&
                    "border-[var(--color-primary-light)] text-[var(--color-primary)]",
                )}
                onClick={() => {
                  if (modalData) {
                    handleModalItemFilter(modalData.title, item);
                  }
                }}
              >
                {item}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <GridColumnFilterPanel
        open={filterPanelOpen}
        onOpenChange={setFilterPanelOpen}
        fields={columnFilterFields}
        values={mergedColumnFilters}
        multiple
        onApply={handleApplyColumnFilters}
        onClear={handleClearColumnFilters}
      />

      <DeleteConfirmDialog
        open={!!deleteTarget}
        title="Delete Acquirer"
        entityLabel="acquirer"
        entityName={deleteTarget?.name || "Unknown"}
        details={deleteTarget ? buildAcquirerDeleteDetails(deleteTarget) : []}
        loading={deleting}
        error={deleteError}
        onConfirm={confirmDelete}
        onCancel={closeDeleteDialog}
      />

      <StatusUpdateDialog
        open={!!statusTarget}
        title="Update Acquirer Status"
        entityLabel="acquirer"
        entityName={statusTarget?.name || "Unknown"}
        currentStatus={statusTarget?.status}
        selectedStatus={selectedStatus}
        onStatusChange={setSelectedStatus}
        details={statusTarget ? buildAcquirerStatusDetails(statusTarget) : []}
        loading={statusUpdating}
        error={statusError}
        onConfirm={confirmStatusUpdate}
        onCancel={closeStatusDialog}
      />
    </div>
  );
};

export default Acquirers;
